import { ColumnSizing } from '../tables-context';
import { MRT_ColumnDef } from 'material-react-table';
import { IFormattedBatchesData } from '../../../interfaces/funds';
import { TableCell } from '../../../components/table/TableCell';
import { Link } from 'react-router-dom';
import { getFormattedDate } from '../../../utils/utilities';
import React, { FC } from 'react';
import { RelatedEntityNameLink } from '../../../components/merchants/RelatedEntityNameLink';
import { CellWithCurrency } from '../../../components/table/CellWithCurrency';
import { AdjustmentModalComponent } from '../../../components/batches/AdjustmentModalComponent';
import { WhisperComponent } from '../../../components/developers/page-components/WhisperComponent';
import clsx from 'clsx';
import { useLocation } from 'react-router';
import { ROUTES } from '../../../routing/routesEnums';
import { BatchTableActionButton } from '../../../components/batches/BatchTableActionButton';
import { BATCH_ACTIONS } from '../../../components/batches/BatchDetailsInformationHeader';

export enum UnpostedBatchesTableAccessorKeys {
	capturePeriod = 'capturePeriodFrom',
	fundingChannel = 'fundingChannel',
	totalValue = 'totalValue',
	totalCount = 'totalCount',
	flexChargeFees = 'flexChargeFees',
	flexChargeFeesCount = 'flexChargeFeesCount',
	chargebacks = 'chargebacks',
	chargebacksCount = 'chargebacksCount',
	returns = 'returns',
	returnsCount = 'returnsCount',
	flexChargeChargebackFeesAmount = 'flexChargeChargebackFeesAmount',
	adjustment = 'adjustment',
	payoutAmount = 'payoutAmount',
	reserveHold = 'reserveHold',
	reserveRelease = 'reserveRelease',
	reserveUtilization = 'reserveUtilization',
	sender = 'senderRelatedEntityId',
	receiver = 'receiverRelatedEntityId',
	batchType = 'batchType',
	payoutId = 'payoutId',
}

export const defaultUnpostedBatchesColumnSizing: ColumnSizing = {
	[UnpostedBatchesTableAccessorKeys.capturePeriod]: 70,
	[UnpostedBatchesTableAccessorKeys.fundingChannel]: 60,
	[UnpostedBatchesTableAccessorKeys.batchType]: 50,
	[UnpostedBatchesTableAccessorKeys.sender]: 70,
	[UnpostedBatchesTableAccessorKeys.receiver]: 70,
	[UnpostedBatchesTableAccessorKeys.totalValue]: 60,
	[UnpostedBatchesTableAccessorKeys.totalCount]: 5,
	[UnpostedBatchesTableAccessorKeys.flexChargeFees]: 60,
	[UnpostedBatchesTableAccessorKeys.flexChargeFeesCount]: 5,
	[UnpostedBatchesTableAccessorKeys.chargebacks]: 60,
	[UnpostedBatchesTableAccessorKeys.chargebacksCount]: 5,
	[UnpostedBatchesTableAccessorKeys.returns]: 60,
	[UnpostedBatchesTableAccessorKeys.returnsCount]: 5,
	[UnpostedBatchesTableAccessorKeys.flexChargeChargebackFeesAmount]: 60,
	[UnpostedBatchesTableAccessorKeys.adjustment]: 60,
	[UnpostedBatchesTableAccessorKeys.reserveHold]: 60,
	[UnpostedBatchesTableAccessorKeys.reserveRelease]: 60,
	[UnpostedBatchesTableAccessorKeys.reserveUtilization]: 60,
	[UnpostedBatchesTableAccessorKeys.payoutAmount]: 60,
	[UnpostedBatchesTableAccessorKeys.payoutId]: 60,
};

export const getCapturePeriod = (startDate, endDate): JSX.Element => {
	const whisperText = (
		<>
			<div>{getFormattedDate(startDate, 'MMM dd, yyyy')} -</div>
			<div>{getFormattedDate(endDate, 'MMM dd, yyyy')}</div>
		</>
	);
	return (
		<WhisperComponent
			isTooltip
			whisperText={whisperText}
			mainText={<div>{getFormattedDate(startDate, 'MMM, yyyy')}</div>}
		/>
	);
};

interface IProps {
	children: any;
	batchId: string;
	additionalClass?: string;
}

export const UnpostedBatchesLink: FC<IProps> = (props) => {
	const { children, batchId, additionalClass } = props;

	const { pathname, search } = useLocation();

	return (
		<Link
			to={`${pathname}/${batchId}`}
			state={{ search: `${pathname}${search}` }}
			className={clsx('text-black fs-8 fc-text-overflow', additionalClass)}
		>
			{children}
		</Link>
	);
};

export const getUnpostedPayoutCount = (id: string, currencyCode: string, value: string | number): JSX.Element => {
	return (
		<UnpostedBatchesLink
			batchId={id}
			additionalClass={clsx('fw-bolder', currencyCode && 'pb-5')}
		>
			{value ? value : 0}
		</UnpostedBatchesLink>
	);
};


export const defaultUnpostedBatchesColumns: MRT_ColumnDef<IFormattedBatchesData>[] = [
	{
		accessorKey: UnpostedBatchesTableAccessorKeys.payoutId,
		header: 'Action button',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family"></span>,
		Cell: ({ row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<BatchTableActionButton
					buttonName='PENDING_PAYOUTS.POST_BTN_NAME'
					isDisabled={!row?.original?.canPost}
					batchId={row?.original?.payoutId}
					action={BATCH_ACTIONS.POST}
				/>
			</TableCell>
		)
	},
	{
		accessorKey: UnpostedBatchesTableAccessorKeys.capturePeriod,
		header: 'Capture period',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Capture period</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<UnpostedBatchesLink batchId={row?.original?.payoutId}>
					{getCapturePeriod(renderedCellValue, row?.original?.capturePeriodTo)}
				</UnpostedBatchesLink>
			</TableCell>
		)
	},
	{
		accessorKey: UnpostedBatchesTableAccessorKeys.fundingChannel,
		header: 'Funding Channel',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Funding<br/>Channel</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<UnpostedBatchesLink batchId={row?.original?.payoutId}>
					{renderedCellValue ? renderedCellValue : '-'}
				</UnpostedBatchesLink>
			</TableCell>
		)
	},
	{
		accessorKey: UnpostedBatchesTableAccessorKeys.batchType,
		header: 'Type',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Type</span>,
		Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<UnpostedBatchesLink batchId={row?.original?.payoutId}>
					{renderedCellValue ? renderedCellValue : '-'}
				</UnpostedBatchesLink>
			</TableCell>
		)
	},
	{
		accessorKey: UnpostedBatchesTableAccessorKeys.sender,
		header: 'Sender',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Sender</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
					<RelatedEntityNameLink
						name={row?.original?.senderRelatedEntityDba}
						id={renderedCellValue}
						type={row?.original?.senderRelatedEntityType}
					/>
			</TableCell>
		)
	},
	{
		accessorKey: UnpostedBatchesTableAccessorKeys.receiver,
		header: 'Receiver',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Receiver</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => (
			<TableCell
				additionalClass="d-flex align-items-center main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<RelatedEntityNameLink
					name={row?.original?.receiverRelatedEntityDba}
					id={renderedCellValue}
					type={row?.original?.receiverRelatedEntityType}
				/>
			</TableCell>
		)
	},
	{
		id: 'total',
		header: 'Total value',
		columns: [
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.totalValue,
				header: 'Amount',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Amount</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						<UnpostedBatchesLink batchId={row?.original?.payoutId}>
							<CellWithCurrency
								value={renderedCellValue}
								currencySymbol={row?.original?.currencySymbol}
								currencyCode={row?.original?.currency}
								isPositive={renderedCellValue >= 0}
							/>
						</UnpostedBatchesLink>
					</TableCell>
				)
			},
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.totalCount,
				header: 'Count',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Count</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						{getUnpostedPayoutCount(row?.original?.payoutId, row?.original?.currencyCode, renderedCellValue)}
					</TableCell>
				)
			},
		],
	},
	{
		id: 'flexChargeFees',
		header: 'Processing fees',
		columns: [
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.flexChargeFees,
				header: 'Amount',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Amount</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						<UnpostedBatchesLink batchId={row?.original?.payoutId}>
							<CellWithCurrency
								value={renderedCellValue}
								currencySymbol={row?.original?.currencySymbol}
								currencyCode={row?.original?.currency}
								isPositive={renderedCellValue >= 0}
							/>
						</UnpostedBatchesLink>
					</TableCell>
				)
			},
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.flexChargeFeesCount,
				header: 'Count',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Count</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						{getUnpostedPayoutCount(row?.original?.payoutId, row?.original?.currencyCode, renderedCellValue)}
					</TableCell>
				)
			},
		],
	},
	{
		id: 'flexChargeChargebackFeesAmount',
		header: 'Chargebacks fees',
		columns: [
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.flexChargeChargebackFeesAmount,
				header: 'Amount',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Amount</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						<UnpostedBatchesLink batchId={row?.original?.payoutId}>
							<CellWithCurrency
								value={renderedCellValue}
								currencySymbol={row?.original?.currencySymbol}
								currencyCode={row?.original?.currency}
								isPositive={renderedCellValue >= 0}
							/>
						</UnpostedBatchesLink>
					</TableCell>
				)
			},
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.chargebacksCount,
				header: 'Count',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Count</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						{getUnpostedPayoutCount(row?.original?.payoutId, row?.original?.currencyCode, renderedCellValue)}
					</TableCell>
				)
			},
		],
	},
	{
		id: 'returns',
		header: 'Returns',
		columns: [
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.returns,
				header: 'Amount',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Amount</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						<UnpostedBatchesLink batchId={row?.original?.payoutId}>
							<CellWithCurrency
								value={renderedCellValue}
								currencySymbol={row?.original?.currencySymbol}
								currencyCode={row?.original?.currency}
								isPositive={renderedCellValue >= 0}
							/>
						</UnpostedBatchesLink>
					</TableCell>
				)
			},
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.returnsCount,
				header: 'Count',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Count</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						{getUnpostedPayoutCount(row?.original?.payoutId, row?.original?.currencyCode, renderedCellValue)}
					</TableCell>
				)
			},
		],
	},
	{
		id: 'reserve',
		header: 'Reserve',
		columns: [
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.reserveHold,
				header: 'Hold',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Hold</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-start main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={false}
					>
						<UnpostedBatchesLink batchId={row?.original?.payoutId}>
							<CellWithCurrency
								value={renderedCellValue}
								currencySymbol={row?.original?.currencySymbol}
								currencyCode={row?.original?.currency}
								isPositive={renderedCellValue >= 0}
							/>
						</UnpostedBatchesLink>
					</TableCell>
				)
			},
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.reserveRelease,
				header: 'Release',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Release</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-start main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={false}
					>
						<UnpostedBatchesLink batchId={row?.original?.payoutId}>
							<CellWithCurrency
								value={renderedCellValue}
								currencySymbol={row?.original?.currencySymbol}
								currencyCode={row?.original?.currency}
								isPositive={renderedCellValue >= 0}
							/>
						</UnpostedBatchesLink>
					</TableCell>
				)
			},
			{
				accessorKey: UnpostedBatchesTableAccessorKeys.reserveUtilization,
				header: 'Utilization',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Utilization</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-start main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={false}
					>
						<UnpostedBatchesLink batchId={row?.original?.payoutId}>
							<CellWithCurrency
								value={renderedCellValue}
								currencySymbol={row?.original?.currencySymbol}
								currencyCode={row?.original?.currency}
								isPositive={renderedCellValue >= 0}
							/>
						</UnpostedBatchesLink>
					</TableCell>
				)
			},
		],
	},
		{
			accessorKey: UnpostedBatchesTableAccessorKeys.chargebacks,
			header: 'Chargebacks value',
			enableSorting: false,
			Header: <span className="text-muted fw-bolder main-font-family">Chargebacks value</span>,
			Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
				<TableCell
					additionalClass="d-flex align-items-center main-font-family text-black"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<UnpostedBatchesLink batchId={row?.original?.payoutId}>
						<CellWithCurrency
							value={renderedCellValue}
							currencySymbol={row?.original?.currencySymbol}
							currencyCode={row?.original?.currency}
							isPositive={renderedCellValue === 0}
						/>
					</UnpostedBatchesLink>
				</TableCell>
			)
		},
		{
			accessorKey: UnpostedBatchesTableAccessorKeys.adjustment,
			header: 'Adjustment',
			enableSorting: false,
			Header: <span className="text-muted fw-bolder main-font-family">Adjustment</span>,
			Cell: ({ renderedCellValue, row, table }: any): JSX.Element => {
				const { pathname } = useLocation();
				return (
					<TableCell
						additionalClass="d-flex align-items-start main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						{
							pathname === `${ROUTES.FUNDS}${ROUTES.BATCHES}`
								? <AdjustmentModalComponent
									value={renderedCellValue}
									currencySymbol={row?.original?.currencySymbol}
									currencyCode={row?.original?.currency}
									paymentId={row?.original?.batchId}
								/>
								: <CellWithCurrency
									value={renderedCellValue}
									currencySymbol={row?.original?.currencySymbol}
									currencyCode={row?.original?.currency}
									isPositive={renderedCellValue >= 0}
								/>
						}

					</TableCell>
				);
			}
		},
		{
				accessorKey: UnpostedBatchesTableAccessorKeys.payoutAmount,
				header: 'Payout',
				enableSorting: false,
				Header: <span className="text-muted fw-bolder main-font-family">Payout</span>,
				Cell: ({ renderedCellValue, row, table }: any): JSX.Element => (
					<TableCell
						additionalClass="d-flex align-items-center main-font-family text-black"
						density={table?.options?.state?.density}
						isTextOverflow={true}
					>
						<UnpostedBatchesLink batchId={row?.original?.payoutId}>
							<CellWithCurrency
								value={renderedCellValue}
								currencySymbol={row?.original?.currencySymbol}
								currencyCode={row?.original?.currency}
								isPositive={renderedCellValue >= 0}
							/>
						</UnpostedBatchesLink>
					</TableCell>
				)
			},
];
